import React from 'react';
import { Box, Typography, Button, useTheme, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Shared hooks
import { useAuth } from '../../hooks/auth/useAuth';

// 404 image from public directory
const image = '/images/404-notfound.avif';

/**
 * RBAC-Ready Not Found Page Component
 * Provides intelligent navigation based on user role and authentication status
 */
const NotFoundPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  /**
   * Determine the appropriate home route based on user role
   * This integrates with your RBAC system for intelligent redirection
   */
  const getHomeRoute = () => {
    if (!isAuthenticated || !user) {
      return '/login';
    }

    // TODO: Implement proper role-based routing when RBAC permissions are fully integrated
    // For now, redirect to dashboard which handles role-based redirects
    return '/dashboard';
  };

  const handleGoHome = () => {
    navigate(getHomeRoute());
  };

  const handleGoBack = () => {
    // Check if there's a previous page in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        padding: 3,
        textAlign: 'center',
        position: 'relative',
        // Add subtle background pattern
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 25% 25%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%), 
                           radial-gradient(circle at 75% 75%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Content container */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* 404 Image */}
        <Box
          component="img"
          src={image}
          alt="404 - Page Not Found"
          sx={{
            width: { xs: '60%', sm: '40%', md: '30%' },
            maxWidth: 300,
            height: 'auto',
            mb: 4,
            filter: theme.palette.mode === 'dark' ? 'brightness(0.8) contrast(1.1)' : 'none',
            transition: 'filter 0.3s ease',
          }}
        />

        {/* Error Message */}
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
            fontSize: { xs: '2rem', sm: '3rem', md: '3.5rem' },
          }}
        >
          404
        </Typography>

        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            mb: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
          }}
        >
          Page Not Found
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            mb: 4,
            maxWidth: 500,
            mx: 'auto',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            lineHeight: 1.6,
          }}
        >
          Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or you
          entered the wrong URL.
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGoHome}
            startIcon={<HomeIcon />}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isAuthenticated ? 'Go to Dashboard' : 'Go to Login'}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: alpha(theme.palette.primary.main, 0.5),
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Go Back
          </Button>
        </Box>

        {/* Additional Help Text */}
        {isAuthenticated && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              mt: 3,
              display: 'block',
              fontSize: '0.75rem',
            }}
          >
            If you believe this is an error, please contact your system administrator.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default NotFoundPage;
